import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { ChatListItem } from '@/types/chat';
import { realtimeCentral } from '@/core/RealtimeCentral';
import { debugLogger } from '@/utils/debugLogger';

// Cache para evitar múltiplas chamadas simultâneas
const conversationCacheByUsername = new Map<string, { id: string; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

export function useChat() {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const queryClient = useQueryClient();

  const fetchProfiles = useCallback(async (userIds: string[]) => {
    if (!userIds.length) return [];

    // ✅ Usar RPC batch ao invés de query direta
    const { data, error } = await (supabase as any)
      .rpc('get_profiles_batch', { profile_ids: userIds });

    if (error) {
      debugLogger.error('Error fetching profiles:', error);
      return [];
    }

    // Type assertion para garantir compatibilidade com código existente
    return (data || []) as Array<{
      id: string;
      username: string;
      main_photo_url: string;
      hidden: boolean;
      onboarding_completed: boolean;
      is_online: boolean;
      last_seen: string;
      last_activity_at: string;
      role: string;
      verification_level: string;
    }>;
  }, []);

  const fetchConversations = useCallback(async (): Promise<ChatListItem[]> => {
    if (!user?.id) return [];

    // Phase 7: Use RPC to avoid exposing UUIDs
    const { data: rpcData, error } = await supabase.rpc('get_user_conversations');
    
    // Transform RPC data to match expected structure
    const data = rpcData?.map(conv => ({
      id: conv.id,
      created_at: conv.created_at,
      updated_at: conv.updated_at,
      user1_id: conv.user1_id,
      user2_id: conv.user2_id,
      last_message_text: conv.last_message_text,
      last_message_time: conv.last_message_time,
      unread_count_user1: conv.user1_id === user.id ? conv.unread_count : 0,
      unread_count_user2: conv.user2_id === user.id ? conv.unread_count : 0,
      deleted_at: conv.deleted_at
    }));

    if (error) {
      debugLogger.error('Error fetching conversations:', error);
      throw error;
    }

    if (!data?.length) return [];

    // Get all unique user IDs
    const userIds = new Set<string>();
    data.forEach(conv => {
      userIds.add(conv.user1_id);
      userIds.add(conv.user2_id);
    });

    // Fetch profiles for all users
    const profiles = await fetchProfiles(Array.from(userIds));
    const profilesMap = new Map(profiles.map(p => [p.id, p]));

    // Transform conversations to ChatListItem format
    const chatItems: ChatListItem[] = [];
    
    for (const conv of data) {
      // NOVO: Filtrar conversas consigo mesmo
      if (conv.user1_id === conv.user2_id) {
        debugLogger.debug('[useChat] Skipping self-conversation:', conv.id);
        continue;
      }

      const otherUserId = conv.user1_id === user.id ? conv.user2_id : conv.user1_id;
      const otherUserProfile = profilesMap.get(otherUserId);
      
      // Filter out conversations with incomplete users only
      if (!otherUserProfile || 
          otherUserProfile.onboarding_completed !== true) {
        continue;
      }
      
      const unreadCount = conv.user1_id === user.id 
        ? (conv.unread_count_user1 || 0)
        : (conv.unread_count_user2 || 0);

      // Phase 7: Track deleted conversations (deleted_by not available in RPC)
      const isDeleted = conv.deleted_at !== null;

      chatItems.push({
        id: conv.id,
        partner: {
          id: otherUserId,
          name: otherUserProfile.username || 'Usuario',
          username: otherUserProfile.username || '',
          avatar: otherUserProfile.main_photo_url || '',
          isOnline: isDeleted ? false : (otherUserProfile.is_online || false), // Não mostrar online se deletada
          lastSeen: otherUserProfile.last_seen,
          lastActivityAt: otherUserProfile.last_activity_at
        },
        lastMessage: isDeleted ? null : conv.last_message_text, // Não mostrar última mensagem se deletada
        time: conv.last_message_time,
        unread: isDeleted ? 0 : unreadCount, // Não mostrar unread se deletada
        isDeleted: isDeleted // Phase 7: Track deletion status
      });
    }

    // NOVO: Ordenar para colocar conversas ativas primeiro
    chatItems.sort((a, b) => {
      // Conversas ativas primeiro
      if (a.isDeleted && !b.isDeleted) return 1;
      if (!a.isDeleted && b.isDeleted) return -1;
      
      // Dentro do mesmo grupo, ordenar por tempo
      const timeA = new Date(a.time || 0).getTime();
      const timeB = new Date(b.time || 0).getTime();
      return timeB - timeA;
    });

    return chatItems;
  }, [user?.id, fetchProfiles]);

  const searchMessages = useCallback(async (term: string): Promise<ChatListItem[]> => {
    if (!user?.id || !term.trim()) return [];

    debugLogger.debug('[useChat] Searching messages for term:', term);

    try {
      // First, get all messages that match the search term
      const { data: messages, error: messagesError } = await supabase
        .from('messages')
        .select(`
          conversation_id, 
          content,
          conversations!inner(
            id,
            deleted_at
          )
        `)
        .ilike('content', `%${term}%`)
        .is('conversations.deleted_at', null) // Para busca, só mostrar conversas ativas
        .order('created_at', { ascending: false })
        .limit(100);

      if (messagesError) {
        debugLogger.error('Error searching messages:', messagesError);
        throw messagesError;
      }

      debugLogger.debug('[useChat] Found messages:', messages?.length || 0);

      if (!messages?.length) return [];

      // Get unique conversation IDs
      const conversationIds = [...new Set(messages.map(m => m.conversation_id))];
      debugLogger.debug('[useChat] Unique conversation IDs:', conversationIds.length);

      // Get conversations details for these IDs
      const { data: conversations, error: convError } = await supabase
        .from('conversations')
        .select('*')
        .in('id', conversationIds)
        .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
        .is('deleted_at', null); // Para busca, só mostrar conversas ativas

      if (convError) {
        debugLogger.error('Error fetching conversations:', convError);
        throw convError;
      }

      debugLogger.debug('[useChat] Found conversations:', conversations?.length || 0);

      if (!conversations?.length) return [];

      // Get all unique user IDs
      const userIds = new Set<string>();
      conversations.forEach(conv => {
        userIds.add(conv.user1_id);
        userIds.add(conv.user2_id);
      });

      // Fetch profiles
      const profiles = await fetchProfiles(Array.from(userIds));
      const profilesMap = new Map(profiles.map(p => [p.id, p]));

      debugLogger.debug('[useChat] Fetched profiles:', profiles.length);

      // Transform to ChatListItem format
      const chatItems: ChatListItem[] = [];
      
      for (const conv of conversations) {
        // NOVO: Filtrar conversas consigo mesmo também na busca
        if (conv.user1_id === conv.user2_id) {
          debugLogger.debug('[useChat] Skipping self-conversation in search:', conv.id);
          continue;
        }

        const otherUserId = conv.user1_id === user.id ? conv.user2_id : conv.user1_id;
        const otherUserProfile = profilesMap.get(otherUserId);
        
        // Filter out conversations with incomplete users only
        if (!otherUserProfile || 
            otherUserProfile.onboarding_completed !== true) {
          continue;
        }
        
        const unreadCount = conv.user1_id === user.id 
          ? (conv.unread_count_user1 || 0)
          : (conv.unread_count_user2 || 0);

        chatItems.push({
          id: conv.id,
          partner: {
            id: otherUserId,
            name: otherUserProfile.username || 'Usuario',
            username: otherUserProfile.username || '',
            avatar: otherUserProfile.main_photo_url || '',
            hidden: otherUserProfile.hidden,
            isOnline: otherUserProfile.is_online || false,
            lastSeen: otherUserProfile.last_seen,
            lastActivityAt: otherUserProfile.last_activity_at
          },
          lastMessage: conv.last_message_text,
          time: conv.last_message_time,
          unread: unreadCount,
          isDeleted: false // Search só retorna conversas ativas
        });
      }

      debugLogger.debug('[useChat] Final search results:', chatItems.length);
      return chatItems;
    } catch (error) {
      debugLogger.error('[useChat] Error in searchMessages:', error);
      return [];
    }
  }, [user?.id, fetchProfiles]);

  const getConversationByUsername = useCallback(async (username: string): Promise<string | null> => {
    if (!user?.id || !username) return null;

    const cacheKey = `${user.id}-${username}`;
    
    // Verificar cache
    const cached = conversationCacheByUsername.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.id;
    }

    try {
      // First find the user by username
      const { data: targetUser, error: userError } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', username)
        .single();

      if (userError || !targetUser) {
        debugLogger.error('User not found:', userError);
        return null;
      }

      // NOVO: Impedir conversa consigo mesmo via username
      if (targetUser.id === user.id) {
        debugLogger.debug('[useChat] Cannot create conversation with self via username');
        return null;
      }

      // Check if conversation exists
      const { data: existingConv, error: convError } = await supabase
        .from('conversations')
        .select('id')
        .or(`and(user1_id.eq.${user.id},user2_id.eq.${targetUser.id}),and(user1_id.eq.${targetUser.id},user2_id.eq.${user.id})`)
        .maybeSingle();

      if (convError) {
        debugLogger.error('Error checking conversation:', convError);
        return null;
      }

      let conversationId = existingConv?.id;

      if (!conversationId) {
        // Create new conversation
        const { data: newConv, error: createError } = await supabase
          .from('conversations')
          .insert({
            user1_id: user.id,
            user2_id: targetUser.id
          })
          .select('id')
          .single();

        if (createError) {
          debugLogger.error('Error creating conversation:', createError);
          return null;
        }

        conversationId = newConv.id;
      }

      // Update cache
      conversationCacheByUsername.set(cacheKey, {
        id: conversationId,
        timestamp: Date.now()
      });

      return conversationId;
    } catch (error) {
      debugLogger.error('Error in getConversationByUsername:', error);
      return null;
    }
  }, [user?.id]);

  // React Query for conversations
  const { 
    data: conversations = [], 
    isLoading: conversationsLoading, 
    error: conversationsError,
    refetch: refetchConversations 
  } = useQuery({
    queryKey: ['conversations', user?.id],
    queryFn: fetchConversations,
    enabled: !!user?.id,
    staleTime: 10000,
    gcTime: 60000,
  });

  // React Query for message search
  const { 
    data: searchResults = [], 
    isLoading: searchLoading,
    error: searchError 
  } = useQuery({
    queryKey: ['messages', 'search', searchTerm, user?.id],
    queryFn: () => searchMessages(searchTerm),
    enabled: !!user?.id && searchTerm.trim().length > 0,
    staleTime: 10000,
    gcTime: 60000,
  });

  // Setup realtime subscription for conversation updates
  useEffect(() => {
    if (!user?.id) return;

    debugLogger.debug('[useChat] Setting up realtime subscription for conversations');
    
    // Initialize RealtimeCentral
    realtimeCentral.initialize(user.id);

    // Subscribe to conversation updates
    const unsubscribe = realtimeCentral.subscribe('useChat', {
      id: 'useChat',
      onConversationUpdate: (payload) => {
        debugLogger.debug('[useChat] Received conversation update:', payload);
        
        const updatedConversation = payload.new;
        if (!updatedConversation) return;

        // Se a conversa foi soft-deletada, remover do cache
        if (updatedConversation.deleted_at) {
          debugLogger.debug('[useChat] Conversation soft-deleted, removing from cache:', updatedConversation.id);
          
          // Remover da cache local
          queryClient.setQueryData(['conversations', user.id], (oldData: ChatListItem[] = []) => {
            return oldData.filter(conv => conv.id !== updatedConversation.id);
          });
          
          // Invalidar também outras queries relacionadas
          queryClient.invalidateQueries({ queryKey: ['message-search'] });
          return;
        }

        // Sempre invalidar cache para garantir sincronização
        debugLogger.debug('[useChat] Invalidating conversations cache due to realtime update');
        queryClient.invalidateQueries({ queryKey: ['conversations', user.id] });

        // Update the conversations query cache
        queryClient.setQueryData(['conversations', user.id], (oldData: ChatListItem[] = []) => {
          const conversationIndex = oldData.findIndex(conv => conv.id === updatedConversation.id);
          
          if (conversationIndex === -1) {
            // This is a new conversation, refetch to get complete data with profiles
            refetchConversations();
            return oldData;
          }

          // Update existing conversation
          const updatedConversations = [...oldData];
          const existingConv = updatedConversations[conversationIndex];
          
          // Calculate new unread count for current user
          const newUnreadCount = updatedConversation.user1_id === user.id 
            ? (updatedConversation.unread_count_user1 || 0)
            : (updatedConversation.unread_count_user2 || 0);

          // Update the conversation data
          updatedConversations[conversationIndex] = {
            ...existingConv,
            lastMessage: updatedConversation.last_message_text,
            time: updatedConversation.last_message_time,
            unread: newUnreadCount
          };

          // Re-sort by last message time (most recent first)
          updatedConversations.sort((a, b) => {
            const timeA = new Date(a.time || 0).getTime();
            const timeB = new Date(b.time || 0).getTime();
            return timeB - timeA;
          });

          debugLogger.debug('[useChat] Updated conversations cache with new last message');
          return updatedConversations;
        });
      }
    });

    return () => {
      debugLogger.debug('[useChat] Cleaning up realtime subscription');
      unsubscribe();
    };
  }, [user?.id, queryClient, refetchConversations]);

  const createConversation = useCallback(async (otherUserId: string): Promise<string | null> => {
    if (!user?.id) return null;

    // NOVO: Impedir criação de conversa consigo mesmo
    if (user.id === otherUserId) {
      debugLogger.debug('[useChat] Cannot create conversation with self');
      return null;
    }

    try {
      // Check if conversation already exists
      const { data: existingConv } = await supabase
        .from('conversations')
        .select('id')
        .or(`and(user1_id.eq.${user.id},user2_id.eq.${otherUserId}),and(user1_id.eq.${otherUserId},user2_id.eq.${user.id})`)
        .maybeSingle();

      if (existingConv) {
        return existingConv.id;
      }

      // Create new conversation
      const { data, error } = await supabase
        .from('conversations')
        .insert({
          user1_id: user.id,
          user2_id: otherUserId
        })
        .select('id')
        .single();

      if (error) throw error;

      // Refresh conversations list
      refetchConversations();

      return data.id;
    } catch (error) {
      debugLogger.error('Error creating conversation:', error);
      return null;
    }
  }, [user?.id, refetchConversations]);

  // Memoized return value para evitar re-renders
  const returnValue = useMemo(() => ({
    conversations,
    conversationsLoading,
    loading: conversationsLoading,
    error: conversationsError,
    searchResults,
    searchLoading,
    searchTerm,
    setSearchTerm,
    createConversation,
    getConversationByUsername,
    searchMessages,
    refetchConversations
  }), [
    conversations,
    conversationsLoading,
    conversationsError,
    searchResults,
    searchLoading,
    searchTerm,
    setSearchTerm,
    createConversation,
    getConversationByUsername,
    searchMessages,
    refetchConversations
  ]);

  return returnValue;
}
